import { Building2, type LucideIcon } from 'lucide-react'
import { CompanySwitcher } from './CompanySwitcher'
import { useAppAuth } from '@/contexts/app-auth.context'
import { companiesClient } from '@/lib/api'
import type { DomainName } from '@super/shared-types'

const TWENTY_ICONS_BASE_URL = 'https://twenty-icons.com'

type Company = {
  id?: string
  name: string
  logo: LucideIcon | string
  plan: string
  subtitle?: string
}

export function SuperAdminCompanySwitcher() {
  const { switchSuperAdminCompany, currentCompany } = useAppAuth()

  // Fetch companies function for super admin
  const fetchCompanies = async (query?: string): Promise<Company[]> => {
    try {
      // Use the companies API client to search for companies
      const response = await companiesClient.searchCompanies({
        q: query || '',
        limit: 10, // Reasonable limit for the dropdown
      })

      // Transform the API response to match the Company type expected by CompanySwitcher
      const companies = response.companies || []
      return companies.map(company => {
        const domainUrl =
          typeof company.domainName === 'string'
            ? company.domainName
            : (company.domainName as DomainName)?.primaryLinkUrl ||
              company.externalOrgId
        const logoUrl = `${TWENTY_ICONS_BASE_URL}/${domainUrl}`

        return {
          id: company.id,
          name: company.name,
          logo: logoUrl,
          plan: 'Company',
          subtitle: domainUrl,
        }
      })
    } catch (error) {
      console.error('Error fetching companies:', error)
      return []
    }
  }

  /**
   * Handle company selection
   * @param company - The company to switch to
   */
  const handleCompanySelect = async (company: Company) => {
    if (!company.id) {
      console.error('Company ID is required for switching')
      return
    }

    try {
      await switchSuperAdminCompany(company.id)
    } catch (error) {
      console.error('Error switching company:', error)
    }
  }

  const selectedCompany = currentCompany
    ? {
        id: currentCompany.id,
        name: currentCompany.name,
        logo: (() => {
          const domainUrl =
            typeof currentCompany.domainName === 'string'
              ? currentCompany.domainName
              : (currentCompany.domainName as DomainName)?.primaryLinkUrl
          return domainUrl ? `${TWENTY_ICONS_BASE_URL}/${domainUrl}` : Building2
        })(),
        plan: 'Company',
        subtitle: (() => {
          const domainUrl =
            typeof currentCompany.domainName === 'string'
              ? currentCompany.domainName
              : (currentCompany.domainName as DomainName)?.primaryLinkUrl
          return domainUrl || currentCompany.externalOrgId
        })(),
      }
    : null

  return (
    <CompanySwitcher
      fetchCompanies={fetchCompanies}
      loadInitialCompanies={true}
      placeholder="Search companies..."
      onCompanySelect={handleCompanySelect}
      selectedCompany={selectedCompany}
    />
  )
}
